// 瀏覽器兼容的 Google Cloud Storage 上傳服務
// 使用 Google Cloud Storage REST API 進行直接上傳

import apiService from './apiService';

export interface UploadResult {
  success: boolean;
  url?: string;
  filename?: string;
  originalFilename?: string;  // 🔧 新增：原始檔名
  error?: string;
  metadata?: {
    size: number;
    type: string;
    uploadedAt: string;
  };
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export class VideoUploadService {
  // 使用環境變量，您已經有了正確的 .env.local 配置
  private static readonly BUCKET_NAME = import.meta.env.VITE_GOOGLE_CLOUD_BUCKET_NAME || 'checkplay-videos';
  private static readonly PROJECT_ID = import.meta.env.VITE_GOOGLE_CLOUD_PROJECT_ID || 'checkplay-fs';

  /**
   * 生成預簽名 URL 用於直接上傳
   * 注意：這需要在後端實現，前端無法直接生成
   */
  static async getSignedUrl(filename: string, contentType: string): Promise<{signedUrl: string, filename: string, originalFilename: string} | null> {
    try {
      // 使用 API 服務調用後端 API 來獲取預簽名 URL
      const data = await apiService.post('/api/get-upload-url', {
        filename,
        contentType,
        bucket: this.BUCKET_NAME
      });

      return {
        signedUrl: data.signedUrl,
        filename: data.filename,
        originalFilename: data.originalFilename
      };
    } catch (error) {
      console.error('獲取預簽名 URL 失敗:', error);
      return null;
    }
  }

  /**
   * 使用預簽名 URL 上傳文件
   */
  static async uploadWithSignedUrl(
    file: File,
    signedUrl: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // 監聽上傳進度
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            onProgress({
              loaded: event.loaded,
              total: event.total,
              percentage: Math.round((event.loaded / event.total) * 100)
            });
          }
        });

        // 監聽完成
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // 🔧 修正：使用 UUID 檔名而不是原始檔名
            const gcsFilename = signedUrl.split('/').pop()?.split('?')[0] || file.name;
            const publicUrl = `https://storage.googleapis.com/${this.BUCKET_NAME}/${gcsFilename}`;
            resolve({
              success: true,
              url: publicUrl,
              filename: gcsFilename,  // 🔧 使用 GCS UUID 檔名
              originalFilename: file.name,  // 🔧 保存原始檔名
              metadata: {
                size: file.size,
                type: file.type,
                uploadedAt: new Date().toISOString()
              }
            });
          } else {
            reject(new Error(`上傳失敗: ${xhr.status} ${xhr.statusText}`));
          }
        });

        // 監聽錯誤
        xhr.addEventListener('error', () => {
          reject(new Error('網絡錯誤'));
        });

        // 監聽中止
        xhr.addEventListener('abort', () => {
          reject(new Error('上傳被中止'));
        });

        // 開始上傳
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

    } catch (error) {
      console.error('上傳錯誤:', error);
      return {
        success: false,
        error: `上傳失敗: ${error instanceof Error ? error.message : '未知錯誤'}`
      };
    }
  }

  /**
   * 簡化的上傳方法（使用真實後端 API）
   */
  static async uploadVideo(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    try {
      // 首先獲取預簽名 URL
      const signedUrlResponse = await this.getSignedUrl(file.name, file.type);
      
      if (!signedUrlResponse) {
        throw new Error('無法獲取上傳 URL');
      }

      // 使用預簽名 URL 上傳
      const uploadResult = await this.uploadWithSignedUrl(file, signedUrlResponse.signedUrl, onProgress);
      
      if (uploadResult.success && signedUrlResponse.filename) {
        // 上傳完成後，確認上傳並設置文件權限
        try {
          const confirmResponse = await fetch('/api/confirm-upload', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              filename: signedUrlResponse.filename,  // 使用 UUID 文件名
              originalFilename: signedUrlResponse.originalFilename  // 傳遞原始文件名
            })
          });

          if (confirmResponse.ok) {
            const confirmData = await confirmResponse.json();
            if (confirmData.success) {
              uploadResult.url = confirmData.publicUrl;
            }
          }
        } catch (confirmError) {
          console.warn('確認上傳失敗，但不影響上傳結果:', confirmError);
        }
      }

      return uploadResult;

    } catch (error) {
      console.error('上傳服務錯誤:', error);
      return {
        success: false,
        error: `服務錯誤: ${error instanceof Error ? error.message : '未知錯誤'}`
      };
    }
  }

  /**
   * 檢查文件是否存在
   */
  static async fileExists(filename: string): Promise<boolean> {
    try {
      const response = await fetch(
        `https://storage.googleapis.com/${this.BUCKET_NAME}/${filename}`,
        { method: 'HEAD' }
      );
      return response.ok;
    } catch (error) {
      console.error('檢查文件存在錯誤:', error);
      return false;
    }
  }

  /**
   * 獲取文件的公開 URL
   */
  static getPublicUrl(filename: string): string {
    return `https://storage.googleapis.com/${this.BUCKET_NAME}/${filename}`;
  }
}

export default VideoUploadService;
